import { useEffect, useState } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { Button } from './ui/button'
import { Download, RefreshCw, Sparkles, X } from 'lucide-react'
import { cn } from '../lib/utils'

// Current app version - should match backend APP_VERSION
export const APP_VERSION = '1.0.0'

// Compare versions (returns -1 if v1 < v2, 0 if equal, 1 if v1 > v2)
const compareVersions = (v1, v2) => {
  const parts1 = v1.split('.').map(Number)
  const parts2 = v2.split('.').map(Number)
  
  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0
    const p2 = parts2[i] || 0
    if (p1 < p2) return -1
    if (p1 > p2) return 1
  }
  return 0
}

export default function ForceUpdateModal() {
  const { locale } = useLanguage()
  const [config, setConfig] = useState(null)
  const [showForceUpdate, setShowForceUpdate] = useState(false)
  const [showUpdateBanner, setShowUpdateBanner] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(false)

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/app-config`)
        const data = await response.json()
        setConfig(data)
        
        const currentVersion = APP_VERSION
        const minRequired = data.min_version_required
        const latestVersion = data.version
        
        // Check if force update is needed
        if (data.force_update && compareVersions(currentVersion, minRequired) < 0) {
          setShowForceUpdate(true)
        }
        // Check if optional update is available
        else if (compareVersions(currentVersion, latestVersion) < 0) {
          const dismissed = sessionStorage.getItem('update_banner_dismissed')
          if (!dismissed) {
            setShowUpdateBanner(true)
          }
        }
      } catch (error) {
        console.error('Error checking app version:', error)
      }
    }
    
    checkVersion()
  }, [])

  const handleForceUpdate = () => {
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name))
      })
    }
    localStorage.removeItem('app_version')
    window.location.reload(true)
  }

  const dismissBanner = () => {
    setBannerDismissed(true)
    setShowUpdateBanner(false)
    sessionStorage.setItem('update_banner_dismissed', 'true')
  }

  const updateMessage = locale === 'es-MX' 
    ? config?.update_message 
    : config?.update_message_en

  return (
    <>
      {/* Force Update Modal - Custom implementation for better control */}
      {showForceUpdate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-background rounded-2xl shadow-2xl w-full max-w-xs p-4 space-y-3 animate-in zoom-in-95 duration-200">
            {/* Icon */}
            <div className="flex justify-center">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center animate-pulse">
                <Download className="w-6 h-6 text-white" />
              </div>
            </div>
            
            {/* Title & Message */}
            <div className="text-center">
              <h2 className="text-lg font-semibold">
                {locale === 'es-MX' ? '¡Nueva versión!' : 'New version!'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {updateMessage}
              </p>
            </div>
            
            {/* Version info */}
            <div className="bg-secondary/50 rounded-xl p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  {locale === 'es-MX' ? 'Tu versión' : 'Your version'}
                </span>
                <span className="font-mono">{APP_VERSION}</span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-muted-foreground">
                  {locale === 'es-MX' ? 'Nueva versión' : 'New version'}
                </span>
                <span className="font-mono text-emerald-600 font-medium">{config?.version}</span>
              </div>
            </div>
            
            {/* Button */}
            <Button 
              onClick={handleForceUpdate}
              className="w-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
              size="lg"
              data-testid="force-update-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {locale === 'es-MX' ? 'Actualizar ahora' : 'Update now'}
            </Button>
          </div>
        </div>
      )}

      {/* Optional Update Banner - Can be dismissed */}
      {showUpdateBanner && !bannerDismissed && (
        <div 
          className={cn(
            "fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-4 py-3",
            "animate-in slide-in-from-top duration-300"
          )}
        >
          <div className="flex items-center justify-between max-w-lg mx-auto">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <span className="text-sm font-medium">
                {locale === 'es-MX' 
                  ? `Nueva versión ${config?.version} disponible` 
                  : `New version ${config?.version} available`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleForceUpdate}
                className="h-7 px-3 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
              >
                {locale === 'es-MX' ? 'Actualizar' : 'Update'}
              </Button>
              <button
                onClick={dismissBanner}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
